import { Input, Select } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { useEffect, useMemo, useState } from "react";
import { WidgetComponentProps } from "../types";
import { deriveRecordFormFields } from "./recordFormWidgetSeed";

/** A single editable field of a {@link RecordFormWidgetConfig}. */
export interface RecordFormFieldConfig {
    /**
     * Dotted path where the entered value is written in the created record payload.
     * Top-level fields use a bare key (e.g. "name"); custom-object attributes use the
     * `value.` prefix (e.g. "value.ask", "value.from_person").
     */
    path: string;
    /** Label shown above the input. Defaults to the last path segment. */
    label?: string;
    /** Which control to render. Defaults to "text". */
    input?: "text" | "textarea" | "select";
    /** Options for a `select` input. */
    options?: { label: string; value: string }[];
    /** Placeholder text for text/textarea inputs. */
    placeholder?: string;
    /** When true the field must be non-empty before the form can be submitted. */
    required?: boolean;
    /** Initial value seeded into the field (and restored on reset). */
    default?: string;
}

/**
 * Config for the `record_form` widget — a small form that creates a workspace
 * record when submitted. Generic (works for any record type), but its first use
 * is the operator's "new one-line ask" box, which creates an `operator-intake`
 * custom object in status `new`.
 */
export interface RecordFormWidgetConfig {
    /** Record type to create, e.g. "custom_object", "flow". Posted to `/<record_type>`. */
    record_type: string;
    /** For custom objects: the object_template this record is built on. */
    object_template_id?: string;
    /**
     * Static values merged into every created record (dotted paths supported),
     * e.g. `{ "value.status": "new" }`.
     */
    fixed?: Record<string, unknown>;
    /** The editable form fields, rendered top-to-bottom. */
    fields?: RecordFormFieldConfig[];
    /**
     * Optional name for the created record. Supports the `{timestamp}` token,
     * replaced with an ISO timestamp so generated names stay unique. When omitted,
     * no name is sent and the backend assigns one.
     */
    name_template?: string;
    /** Label on the submit button. Defaults to "Create". */
    submit_label?: string;
    /** Success notification text. Defaults to "Created.". */
    success_message?: string;
    /** Clear the form after a successful submit. Defaults to true. */
    reset_on_success?: boolean;
}

/** Write `value` at a dotted `path` into `target`, creating intermediate objects. */
const setPath = (target: Record<string, any>, path: string, value: unknown) => {
    const segments = path.split(".");
    let node = target;
    for (let i = 0; i < segments.length - 1; i += 1) {
        const key = segments[i];
        if (typeof node[key] !== "object" || node[key] === null) {
            node[key] = {};
        }
        node = node[key];
    }
    node[segments[segments.length - 1]] = value;
};

const initialValues = (fields: RecordFormFieldConfig[]): Record<string, string> => {
    const values: Record<string, string> = {};
    fields.forEach((field) => {
        values[field.path] = field.default ?? "";
    });
    return values;
};

/**
 * `record_form` widget — renders a form and creates a record on submit.
 *
 * Posts directly to the REST resource endpoint (same API the record editor uses,
 * without Redux), mirroring {@link ActionButtonWidget}. Field values are written
 * into the payload by their dotted `path`, so `value.*` paths populate custom-object
 * attributes while bare paths set top-level record fields. On success it shows a
 * notification and (by default) resets the form.
 */
export const RecordFormWidget = ({
    config,
}: WidgetComponentProps<RecordFormWidgetConfig>) => {
    const recordType = config?.record_type;
    const objectTemplateId = config?.object_template_id;
    const configuredFields = config?.fields;
    const hasConfiguredFields = Array.isArray(configuredFields) && configuredFields.length > 0;
    // With no `fields` configured, the form shows the create screen's fields:
    // Name plus the object template's shown string attributes
    // (operator-task-fe-record-form-widget-seed-required-fields-from-create-schema).
    const [objectTemplateSchema, setObjectTemplateSchema] = useState<unknown>(null);
    const needsSchema =
        !hasConfiguredFields && recordType?.toUpperCase() === "CUSTOM_OBJECT" && Boolean(objectTemplateId);
    useEffect(() => {
        if (!needsSchema) {
            setObjectTemplateSchema(null);
            return undefined;
        }
        let cancelled = false;
        restClient
            .get(`object_template/${objectTemplateId}`, { params: { fields: "schema" } })
            .then((response: any) => {
                if (!cancelled) {
                    setObjectTemplateSchema(response?.data?.object_template?.schema ?? null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setObjectTemplateSchema(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [needsSchema, objectTemplateId]);

    const fields = useMemo(
        () =>
            hasConfiguredFields
                ? (configuredFields as RecordFormFieldConfig[])
                : deriveRecordFormFields(objectTemplateSchema, { includeName: !config?.name_template }),
        [hasConfiguredFields, configuredFields, objectTemplateSchema, config?.name_template]
    );
    const [values, setValues] = useState<Record<string, string>>(() => initialValues(fields));
    const [isSubmitting, setSubmitting] = useState(false);
    useEffect(() => {
        setValues(initialValues(fields));
    }, [fields]);

    const submitLabel = config?.submit_label ?? "Create";
    const resetOnSuccess = config?.reset_on_success ?? true;

    const setField = (path: string, value: string) => {
        setValues((prev) => ({ ...prev, [path]: value }));
    };

    const missingRequired = fields.some(
        (field) => field.required && !(values[field.path] ?? "").trim()
    );

    const handleSubmit = async () => {
        if (!recordType) {
            NotificationService.error("record_form widget: record_type is not configured.");
            return;
        }
        if (missingRequired) {
            NotificationService.error("Please fill in all required fields.");
            return;
        }
        setSubmitting(true);
        try {
            const payload: Record<string, any> = {};
            if (config?.object_template_id) {
                payload.object_template_id = config.object_template_id;
            }
            Object.entries(config?.fixed ?? {}).forEach(([path, value]) => {
                setPath(payload, path, value);
            });
            fields.forEach((field) => {
                const raw = values[field.path] ?? "";
                // Skip empty optional fields so we don't overwrite backend defaults.
                if (raw.trim() === "" && !field.required) {
                    return;
                }
                setPath(payload, field.path, raw);
            });
            if (config?.name_template) {
                payload.name = config.name_template.replace(
                    "{timestamp}",
                    new Date().toISOString()
                );
            }

            // record_type is stored in canonical UPPERCASE (e.g. CUSTOM_OBJECT);
            // the REST resource path is lowercase (/custom_object), so normalize
            // before POSTing (dashboardWidgetSchema RECORD_TYPE_OPTIONS are
            // canonical-cased — operator-task-fe-dashboard-widget-form-builder-fixes #1).
            await restClient.post(recordType.toLowerCase(), payload);
            NotificationService.success(config?.success_message ?? "Created.");
            if (resetOnSuccess) {
                setValues(initialValues(fields));
            }
        } catch (err: any) {
            NotificationService.error(<RestErrorNotification error={err} />);
        } finally {
            setSubmitting(false);
        }
    };

    if (!recordType) {
        return <div className="DashboardWidget__error">record_type is not configured.</div>;
    }

    return (
        <div className="DashboardRecordForm">
            <SubmitLock locked={isSubmitting} className="DashboardRecordForm__fields">
                {fields.map((field) => {
                    const label = field.label ?? field.path.split(".").pop() ?? field.path;
                    const value = values[field.path] ?? "";
                    return (
                        <label className="DashboardRecordForm__field" key={field.path}>
                            <span className="DashboardRecordForm__label">
                                {label}
                                {field.required && (
                                    <span className="DashboardRecordForm__required"> *</span>
                                )}
                            </span>
                            {field.input === "textarea" ? (
                                <Input.TextArea
                                    autoSize={{ minRows: 2, maxRows: 8 }}
                                    value={value}
                                    placeholder={field.placeholder}
                                    onChange={(event) => setField(field.path, event.target.value)}
                                />
                            ) : field.input === "select" ? (
                                <Select
                                    style={{ width: "100%" }}
                                    value={value || undefined}
                                    placeholder={field.placeholder}
                                    options={field.options ?? []}
                                    allowClear={!field.required}
                                    onChange={(next) => setField(field.path, next ?? "")}
                                />
                            ) : (
                                <Input
                                    value={value}
                                    placeholder={field.placeholder}
                                    onChange={(event) => setField(field.path, event.target.value)}
                                />
                            )}
                        </label>
                    );
                })}
            </SubmitLock>
            <div className="DashboardRecordForm__actions">
                <Button
                    variation="primary"
                    label={submitLabel}
                    icon={IconCollection.plus}
                    loading={isSubmitting}
                    disabled={isSubmitting || missingRequired}
                    onClick={handleSubmit}
                />
            </div>
        </div>
    );
};
